import React, { useEffect, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";

import Select from "../select/select";
import Stack from "../stack/stack";
import Text from "../text/text";

type Period = "AM" | "PM";

interface TimepickerProps {
  onChange(date: Date): void;
  futureOnly?: boolean;
  value?: Date;
  minMinutesAhead?: number;
}

const BASE_HOURS = Array.from({ length: 12 }, (_, i) => i + 1);
const BASE_MINUTES = Array.from({ length: 60 }, (_, i) => i);
const ALL_PERIODS: Period[] = ["AM", "PM"];

const to24Hour = (hour: number, period: Period): number =>
  period === "PM" ? (hour % 12) + 12 : hour % 12;

const withTime = (
  base: Date,
  hour: number,
  minute: number,
  period: Period,
): Date => {
  const next = new Date(base);
  next.setHours(to24Hour(hour, period), minute, 0, 0);
  return next;
};

/**
 * Timepicker that allows a user to select hour, minute, and period.
 */
const Timepicker = ({
  onChange,
  futureOnly = false,
  value,
  minMinutesAhead,
}: TimepickerProps) => {
  // The earliest selectable datetime — snapshot once at mount.
  // Equals now + minMinutesAhead (or now if not provided).
  const [floor] = useState(
    () => new Date(Date.now() + (minMinutesAhead ?? 0) * 60 * 1000),
  );
  const [selectedDate, setSelectedDate] = useState<Date>(() => value ?? floor);

  const hour = selectedDate.getHours() % 12 || 12;
  const minute = selectedDate.getMinutes();
  const period: Period = selectedDate.getHours() >= 12 ? "PM" : "AM";

  const available = useMemo(() => {
    const sameDayAsFloor = selectedDate.toDateString() === floor.toDateString();
    if (!futureOnly || !sameDayAsFloor) {
      return { hours: BASE_HOURS, minutes: BASE_MINUTES, periods: ALL_PERIODS };
    }

    const floorHour = floor.getHours();
    const floorMinute = floor.getMinutes();
    const floorPeriod: Period = floorHour >= 12 ? "PM" : "AM";

    const periods: Period[] = floorPeriod === "AM" ? ALL_PERIODS : ["PM"];
    const hours = BASE_HOURS.filter(
      (hour) => to24Hour(hour, period) >= floorHour,
    );
    const selectedHour24 = to24Hour(hour, period);
    const minutes =
      selectedHour24 === floorHour
        ? BASE_MINUTES.filter((minute) => minute >= floorMinute)
        : BASE_MINUTES;

    return { hours, minutes, periods };
  }, [futureOnly, hour, period, selectedDate, floor]);

  const handleChange = (
    changes: Partial<{ hour: number; minute: number; period: Period }>,
  ) => {
    const next = withTime(
      selectedDate,
      changes.hour ?? hour,
      changes.minute ?? minute,
      changes.period ?? period,
    );
    setSelectedDate(next);
    onChange(next);
  };

  // Value sync runs before auto-correct so that when both fire in the same
  // commit phase, auto-correct's update wins (otherwise it gets undone).
  useEffect(() => {
    if (!value) return;
    setSelectedDate((prev) =>
      value.getTime() !== prev.getTime() ? value : prev,
    );
  }, [value]);

  useEffect(() => {
    if (!futureOnly) return;

    let corrected: Date | undefined;
    if (!available.periods.includes(period)) {
      corrected = withTime(selectedDate, hour, minute, available.periods[0]);
    } else if (!available.hours.includes(hour)) {
      corrected = withTime(selectedDate, available.hours[0], minute, period);
    } else if (!available.minutes.includes(minute)) {
      corrected = withTime(selectedDate, hour, available.minutes[0], period);
    }

    if (corrected) {
      setSelectedDate(corrected);
      onChange(corrected);
    }
  }, [hour, minute, period, futureOnly, available, selectedDate, onChange]);

  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 sm:gap-4">
      <div className="sm:col-span-1">
        <Stack>
          <Text size="sm" weight="medium" theme="muted" className="sr-only">
            <FormattedMessage id="timepicker.hour" defaultMessage="Hour" />
          </Text>

          <Select
            value={hour}
            onChange={(e) => handleChange({ hour: Number(e.target.value) })}
            data-testid="timepicker-hour"
          >
            {available.hours.map((hour) => (
              <option key={hour} value={hour}>
                {hour}
              </option>
            ))}
          </Select>
        </Stack>
      </div>

      <div className="sm:col-span-1">
        <Stack>
          <Text size="sm" weight="medium" theme="muted" className="sr-only">
            <FormattedMessage id="timepicker.minute" defaultMessage="Minute" />
          </Text>

          <Select
            value={minute}
            onChange={(e) => handleChange({ minute: Number(e.target.value) })}
            data-testid="timepicker-minute"
          >
            {available.minutes.map((minute) => (
              <option key={minute} value={minute}>
                {minute.toString().padStart(2, "0")}
              </option>
            ))}
          </Select>
        </Stack>
      </div>

      <div className="sm:col-span-1">
        <Stack>
          <Text size="sm" weight="medium" theme="muted" className="sr-only">
            <FormattedMessage id="timepicker.period" defaultMessage="AM/PM" />
          </Text>

          <Select
            value={period}
            onChange={(e) => handleChange({ period: e.target.value as Period })}
            data-testid="timepicker-period"
          >
            {available.periods.map((period) => (
              <option key={period} value={period}>
                {period}
              </option>
            ))}
          </Select>
        </Stack>
      </div>
    </div>
  );
};

export default Timepicker;
